<template>
  <!-- Leaving, in three steps: why (the team reads it), what we can do about it (an answer to that reason, never forced),
       what happens next (dates, orders, data). The cancellation runs at the end of the period and can be withdrawn
       until then. Opened from « Solde et forfait » (composables/useSubscription.js, openCancel). -->
  <UModal v-model:open="open" :title="t('billing.cancel.title')" :description="stepLabel" :ui="{ content: 'sm:max-w-lg' }">
    <template #body>
      <!-- 1. Why -->
      <fieldset v-if="step === 'reason'" class="cp-fieldset">
        <legend class="cp-title">{{ t("billing.cancel.reason.title") }}</legend>
        <p class="cp-text">{{ t("billing.cancel.reason.lead") }}</p>
        <div class="cp-reasons">
          <label v-for="option in CANCELLATION_REASONS" :key="option.key" class="cp-reason" :class="{ 'cp-reason--on': reason === option.key }">
            <input v-model="reason" class="cp-reason__input" type="radio" name="cancel-reason" :value="option.key">
            <UIcon :name="option.icon" class="cp-reason__icon" aria-hidden="true" />
            <span>{{ t(`billing.cancel.reason.options.${ option.key }`) }}</span>
          </label>
        </div>
        <UFormField v-if="reason" :label="t('billing.cancel.reason.commentLabel')" class="cp-comment">
          <UTextarea v-model="comment" :rows="3" :maxlength="COMMENT_MAX" autoresize class="w-full" :placeholder="commentPlaceholder" />
        </UFormField>
      </fieldset>

      <!-- 2. What we can do about it -->
      <div v-else-if="step === 'answer'" class="cp-answer">
        <span class="cp-answer__icon" aria-hidden="true"><UIcon :name="ANSWER_ICONS[answer.kind]" /></span>
        <p class="cp-title">{{ answerTitle }}</p>
        <p class="cp-text">{{ answerText }}</p>
        <UButton v-if="answer.kind === 'cheaperPlan'" color="neutral" icon="i-heroicons-arrow-down-circle" :loading="opening" @click="takeCheaperPlan">
          {{ t("billing.cancel.answer.cheaperPlan.action", { plan: cheaperName }) }}
        </UButton>
        <UButton v-else-if="answer.kind === 'callback'" color="neutral" icon="i-heroicons-phone" @click="talk({ callback: true })">
          {{ t("billing.cancel.answer.callback.action") }}
        </UButton>
        <UButton v-else-if="answer.kind === 'write'" color="neutral" icon="i-heroicons-chat-bubble-left-right" @click="talk()">
          {{ t("billing.cancel.answer.write.action") }}
        </UButton>
      </div>

      <!-- 3. What happens next -->
      <div v-else-if="step === 'confirm'" class="cp-confirm">
        <p class="cp-title">{{ t("billing.cancel.confirm.title") }}</p>
        <ul class="cp-facts">
          <li><UIcon name="i-heroicons-calendar" aria-hidden="true" />{{ untilLine }}</li>
          <li><UIcon name="i-heroicons-no-symbol" aria-hidden="true" />{{ t("billing.cancel.confirm.after") }}</li>
          <li><UIcon name="i-heroicons-truck" aria-hidden="true" />{{ t("billing.cancel.confirm.orders") }}</li>
          <li><UIcon name="i-heroicons-archive-box" aria-hidden="true" />{{ t("billing.cancel.confirm.keep") }}</li>
          <li><UIcon name="i-heroicons-arrow-uturn-left" aria-hidden="true" />{{ t("billing.cancel.confirm.undo") }}</li>
        </ul>
        <p v-if="error" class="cp-alert" role="alert">{{ errorText }}</p>
      </div>

      <!-- Done -->
      <div v-else class="cp-done" role="status">
        <span class="cp-done__icon" aria-hidden="true"><UIcon name="i-heroicons-check" /></span>
        <p class="cp-title">{{ t("billing.cancel.done.title") }}</p>
        <p class="cp-text">{{ doneText }}</p>
        <p class="cp-text cp-text--small">{{ t("billing.cancel.done.undo") }}</p>
      </div>
    </template>

    <template #footer>
      <div class="flex w-full flex-wrap justify-between gap-2">
        <template v-if="step === 'reason'">
          <span />
          <div class="flex gap-2">
            <UButton color="neutral" variant="outline" @click="open = false">{{ t("billing.cancel.actions.keep") }}</UButton>
            <UButton color="neutral" :disabled="!reason" trailing-icon="i-heroicons-arrow-right" @click="toAnswer">
              {{ t("billing.cancel.actions.next") }}
            </UButton>
          </div>
        </template>
        <template v-else-if="step === 'answer'">
          <UButton color="neutral" variant="ghost" icon="i-heroicons-arrow-left" @click="step = 'reason'">{{ t("billing.cancel.actions.back") }}</UButton>
          <UButton color="neutral" variant="outline" @click="toConfirm">{{ t("billing.cancel.actions.continue") }}</UButton>
        </template>
        <template v-else-if="step === 'confirm'">
          <UButton color="neutral" variant="ghost" icon="i-heroicons-arrow-left" :disabled="sending" @click="step = 'answer'">
            {{ t("billing.cancel.actions.back") }}
          </UButton>
          <div class="flex flex-wrap gap-2">
            <UButton color="neutral" :disabled="sending" @click="open = false">{{ t("billing.cancel.actions.keep") }}</UButton>
            <UButton v-if="error === 'manual'" color="neutral" variant="outline" icon="i-heroicons-chat-bubble-left-right" @click="talk({ manual: true })">
              {{ t("billing.cancel.errors.manualAction") }}
            </UButton>
            <UButton v-else color="error" variant="outline" :loading="sending" @click="confirm">{{ t("billing.cancel.actions.confirm") }}</UButton>
          </div>
        </template>
        <template v-else>
          <span />
          <UButton color="neutral" @click="open = false">{{ t("billing.cancel.actions.close") }}</UButton>
        </template>
      </div>
    </template>
  </UModal>
</template>

<script setup>
import { fetch } from "~/utils/helper";
import { text2Price } from "~/utils/global.mjs";
import { CANCELLATION_REASONS, OFFBOARDING_HELP_TOPIC, longDate, reasonOf, retentionFor } from "~/utils/billing";
import { plainName } from "~/utils/welcome";
import { CONTACT_EMAIL } from "~/composables/useHelpDesk";

const COMMENT_MAX = 1000;
const ANSWER_ICONS = {
  cheaperPlan: "i-heroicons-arrow-down-circle",
  callback: "i-heroicons-phone",
  write: "i-heroicons-chat-bubble-left-right",
  none: "i-heroicons-archive-box"
};

const { t, locale } = useI18n();
const { summary, load, cancel, cancelOpen } = useSubscription();
const { ask } = useHelpDesk();
const { trackCancellation } = useTracking();

const open = computed({
  get: () => cancelOpen.value,
  set: (value) => { cancelOpen.value = value; }
});

const step = ref("reason"); // reason | answer | confirm | done
const reason = ref(null);
const comment = ref("");
const answer = ref({ kind: "none" });
const plans = ref([]);
const sending = ref(false);
const opening = ref(false);
const error = ref(null); // null | "manual" | "forbidden" | "failed"

// Each opening starts over, unless it ended on « done »: then it says the cancellation is recorded.
watch(open, (isOpen) => {
  if (!isOpen) return;
  if (step.value !== "done" || !summary.value?.endsAt) {
    step.value = "reason";
    reason.value = null;
    comment.value = "";
  }
  error.value = null;
  if (!summary.value) load();
});

const stepLabel = computed(() => {
  const current = { reason: 1, answer: 2, confirm: 3 }[step.value];
  return current ? t("billing.cancel.step", { current, total: 3 }) : "";
});
const commentPlaceholder = computed(() => {
  const key = reasonOf(reason.value)?.comment ? reason.value : "default";
  return t(`billing.cancel.reason.comment.${ key }`);
});

// The plans, to answer « trop cher » with a lighter one when there is one.
const loadPlans = async () => {
  if (plans.value.length) return;
  try {
    const res = await fetch("/subscriptions");
    plans.value = res?.subscriptions?.available ?? [];
  } catch { /* no lighter plan offered: a person is, instead */ }
};

const toAnswer = async () => {
  if (reason.value === "too_expensive") await loadPlans();
  answer.value = retentionFor(reason.value, { plans: plans.value, currentId: summary.value?.plan?.id ?? null });
  trackCancellation({ step: "reason", reason: reason.value, answer: answer.value.kind });
  step.value = "answer";
};
const toConfirm = () => {
  trackCancellation({ step: "answer_declined", reason: reason.value, answer: answer.value.kind });
  // A plan the team gave is cancelled by the team: the last step says so at once and offers to write to it.
  error.value = summary.value?.billing === "manual" ? "manual" : null;
  step.value = "confirm";
};

const planLabel = (displayname) => {
  const label = t(`subscriptions.${ displayname }`);
  return plainName(label === `subscriptions.${ displayname }` ? displayname : label);
};
const cheaperName = computed(() => (answer.value.plan ? planLabel(answer.value.plan.displayname) : ""));
const answerTitle = computed(() => t(`billing.cancel.answer.${ answer.value.kind }.title`));
const answerText = computed(() => {
  const { kind, plan } = answer.value;
  if (kind === "cheaperPlan") return t("billing.cancel.answer.cheaperPlan.text", { plan: cheaperName.value, price: text2Price(plan.price, 0) });
  if (kind === "callback" && reason.value === "too_complex") return t("billing.cancel.answer.callback.textComplex");
  return t(`billing.cancel.answer.${ kind }.text`);
});

// The plan changes on Stripe's portal, opened on the change of the subscription; back on the dashboard afterwards.
const takeCheaperPlan = async () => {
  opening.value = true;
  trackCancellation({ step: "answer_taken", reason: reason.value, answer: "cheaperPlan" });
  try {
    const res = await fetch("/accounts/:accountId/payments/portal", { method: "POST", body: { redirect: window.location.href, flow: "update" } });
    if (res?.portal) window.location.href = res.portal;
  } catch {
    opening.value = false;
    talk();
  }
};

// The question goes to the team with the reason and the comment: the person does not have to say it twice.
const talk = ({ callback = false, manual = false } = {}) => {
  if (!manual) trackCancellation({ step: "answer_taken", reason: reason.value, answer: callback ? "callback" : "write" });
  const lines = [
    manual ? t("billing.cancel.errors.manualMessage") : "",
    `${ t("billing.cancel.reason.title") } ${ t(`billing.cancel.reason.options.${ reason.value }`) }`,
    comment.value.trim()
  ].filter(Boolean);
  open.value = false;
  ask(OFFBOARDING_HELP_TOPIC, { callback, message: lines.join("\n") });
};

const untilLine = computed(() => {
  const s = summary.value;
  const date = s?.status === "trialing" ? s?.trialEndsAt : s?.renewsAt;
  if (!date) return t("billing.cancel.confirm.untilUnknown");
  return t(s.status === "trialing" ? "billing.cancel.confirm.untilTrial" : "billing.cancel.confirm.until", { date: longDate(date, locale.value) });
});

const confirm = async () => {
  if (sending.value) return;
  // A plan the team gave is not cancelled here: say so before asking, rather than after a refusal.
  if (summary.value?.billing === "manual") {
    error.value = "manual";
    return;
  }
  sending.value = true;
  error.value = null;
  const outcome = await cancel({ reason: reason.value, comment: comment.value.trim() });
  sending.value = false;
  if (outcome === "done") {
    trackCancellation({ step: "confirmed", reason: reason.value });
    step.value = "done";
  } else {
    error.value = outcome;
  }
};

const errorText = computed(() => {
  if (error.value === "manual") return t("billing.cancel.errors.manual");
  if (error.value === "forbidden") return t("billing.cancel.errors.forbidden");
  return t("billing.cancel.errors.failed", { email: CONTACT_EMAIL });
});
const doneText = computed(() => {
  const date = summary.value?.endsAt;
  return date ? t("billing.cancel.done.text", { date: longDate(date, locale.value) }) : t("billing.cancel.done.textUnknown");
});
</script>

<style lang="scss" scoped>
// Same grammar as the getting started (assets/scss/pages/onboarding.scss): headings #344767, text #67748e.
.cp-fieldset {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cp-title {
  color: #344767;
  font-size: 16px;
  font-weight: 600;
}

.cp-text {
  color: #67748e;
  font-size: 14px;
  line-height: 1.55;

  &--small {
    font-size: 13px;
  }
}

.cp-reasons {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}

.cp-reason {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 8px 14px;
  border: 1px solid #dfe4f0;
  border-radius: 10px;
  color: #344767;
  font-size: 14px;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;

  &:hover {
    border-color: #4553c9;
  }

  &--on {
    border-color: #4553c9;
    background: #f4f5ff;
    color: #1d2a8f;
    font-weight: 500;
  }

  &:has(.cp-reason__input:focus-visible) {
    outline: 2px solid #4553c9;
    outline-offset: 2px;
  }

  &__input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  &__icon {
    flex: none;
    font-size: 18px;
    color: #4553c9;
  }
}

.cp-comment {
  margin-top: 10px;
}

.cp-answer,
.cp-done {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;

  &__icon {
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: #f4f5ff;
    color: #1d2a8f;
    font-size: 22px;
  }
}

.cp-done__icon {
  border-radius: 999px;
  background: #e6f4ec;
  color: #1b6b47;
}

.cp-facts {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
  color: #344767;
  font-size: 14px;
  line-height: 1.5;

  li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  :deep(svg),
  :deep(.iconify) {
    flex: none;
    margin-top: 2px;
    color: #67748e;
  }
}

.cp-alert {
  margin-top: 14px;
  padding: 12px 16px;
  border-radius: 10px;
  background: #fff4e0;
  color: #7a4a00;
  font-size: 13.5px;
}

@media (prefers-reduced-motion: reduce) {
  .cp-reason {
    transition: none;
  }
}
</style>
